import React, {memo, forwardRef, useMemo} from 'react';
import MenuItem from 'components/ui/Menu/MenuItem';
import MenuRow from 'components/ui/Menu/MenuRow';
import {Check} from 'lucide-react';
import {twMerge} from 'tailwind-merge';
import {getMenuItemIconSizeClass} from 'utils/uiSizes';

/**
 * DropdownMenuItem - Menu item for DropdownMenu
 * Wraps MenuItem to provide dropdown-specific API (children + onClick with event)
 *
 * @typedef {Object} DropdownMenuItemProps
 * @property {React.ReactNode} children - Content of the menu item
 * @property {boolean} [highlighted] - Whether the item is highlighted (keyboard focus)
 * @property {boolean} [disabled] - Whether the item is disabled
 * @property {boolean} [selected] - Whether the item is selected (shows checkmark)
 * @property {Function} [onClick] - Callback when item is clicked: (event) => void
 * @property {string} [href] - URL for link navigation (renders as anchor tag)
 * @property {string} [target] - Link target attribute (e.g., '_blank', '_self')
 * @property {string} [size] - Size variant: 'xs', 'sm', 'md', 'lg' (default: 'md')
 * @property {string} [className] - Additional CSS classes
 * @property {string} [id] - Item ID for aria-activedescendant
 * @property {number} [index] - Item index (for auto-generating ID)
 */
const DropdownMenuItem = memo(
	forwardRef(
		(
			{
				children,
				highlighted = false,
				disabled = false,
				selected = false,
				onClick,
				href,
				target,
				size = 'md',
				className,
				id,
				index,
				...props
			},
			ref,
		) => {
			const value = id || (index !== undefined ? `dropdown-menu-item-${index}` : '');
			const iconSizeClass = getMenuItemIconSizeClass(size);

			// Adapt onClick signature: MenuItem expects (option, value), DropdownMenuItem uses (event)
			const handleMenuItemClick = useMemo(
				() => (_opt, _val) => {
					const syntheticEvent = {
						preventDefault: () => {},
						stopPropagation: () => {},
						target: {value},
					};
					onClick?.(syntheticEvent);
				},
				[onClick, value],
			);

			// Handle click for anchor tags
			const handleAnchorClick = useMemo(
				() => event => {
					if (!disabled) {
						onClick?.(event);
					}
				},
				[onClick, disabled],
			);

			// If href is provided, render as anchor tag using MenuRow
			if (href) {
				return (
					<MenuRow
						ref={ref}
						as="a"
						href={href}
						target={target}
						onClick={handleAnchorClick}
						highlighted={highlighted}
						disabled={disabled}
						size={size}
						variant="pill"
						className={twMerge(
							selected && 'text-gray-900 dark:text-gray-100 font-medium',
							className,
						)}
						role="menuitem"
						aria-disabled={disabled}
						id={id || (index !== undefined ? `dropdown-menu-item-${index}` : undefined)}
						{...props}
					>
						<span className="flex-1 flex items-center gap-2 truncate">{children}</span>
						{selected && (
							<Check
								className={twMerge(
									iconSizeClass,
									'text-blue-600 dark:text-blue-400 ml-2',
								)}
							/>
						)}
					</MenuRow>
				);
			}

			// Default: render as MenuItem (button)
			return (
				<MenuItem
					ref={ref}
					option={null}
					index={index}
					getOptionValue={() => value}
					getOptionLabel={() => ''}
					highlighted={highlighted}
					selected={selected}
					disabled={disabled}
					onClick={handleMenuItemClick}
					showCheck={true}
					size={size}
					variant="pill"
					className={className}
					{...props}
					role="menuitem"
					id={id || (index !== undefined ? `dropdown-menu-item-${index}` : undefined)}
				>
					{children}
				</MenuItem>
			);
		},
	),
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

export default DropdownMenuItem;
